import { useEffect } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import Builder from "./Builder";
import { asset } from "./assets";
import { HOSTELS, TRIPS } from "./content";
import Hero from "./Hero";
import { appUrl, HOME_PATH, hostelOfSearch, MM_HOME_URL } from "./links";
import { FILTER_ROUTE } from "./mapModel";
import { defNights } from "./plannerLogic";
import { setRoute } from "./routeStore";
import TripCard from "./TripCard";
import { Arrow } from "./ui";
import styles from "./MadMonkeyHome.module.scss";
import shared from "./MadMonkey.module.scss";

const FAQ: [string, string][] = [
	["What does it cost?", "Nothing. The map, the route builder, the day-by-day plan and planning with friends are free, in the app and on the web."],
	[
		"How do I build a route?",
		"Hit the big + or tap a monkey on the map. Set the nights, drag stops into order, then tap Get this trip and it lands in Stippl.",
	],
	["Can I go off-script?", "Always. Every route is your own copy: swap a hostel, stay longer, add the waterfall in between. Nothing is locked."],
	[
		"Where do I book the beds?",
		"Your call. Book most Mad Monkeys right inside Stippl so each bed sits on the right night, or book direct on madmonkeyhostels.com and add it to your trip after.",
	],
	[
		"How do I get from hostel to hostel?",
		"Bus, minivan, boat or a short flight. Each route shows the usual way, and the tour desk at every Mad Monkey books the next leg.",
	],
	["What's The Meet Up?", "The evening get-together at every Mad Monkey bar. Time your arrivals for it and you'll never start a new town alone."],
	["Can my new travel crew join?", "Yes. Invite them to your trip and plan the next stop together on one map."],
	["Which countries?", "Six: Thailand, Laos, Vietnam, Cambodia, Indonesia and the Philippines. 23 Mad Monkeys, all on the map above."],
];

/** What a "start a route here" or "tweak this route" link leaves for the home page to open on. */
export interface HomeNavState {
	filter?: string;
	hostel?: string;
}

const MadMonkeyHome = () => {
	const { state: navState, search, pathname, hash } = useLocation();
	const history = useHistory();
	const state = (navState ?? {}) as HomeNavState;
	// A "start a route here" from a page framed on another site names the hostel in the address (`startPath`), as `location.state` does for one of our own pages.
	const queryHostel = state.hostel ? null : hostelOfSearch(search);
	useEffect(() => {
		if (!queryHostel) return;
		// What the link on our own page does before it navigates (HostelPage's `startHere`), then the address becomes that page's state: a reload keeps the hostel open and does not start the route again.
		setRoute([{ s: queryHostel, n: defNights(queryHostel) }]);
		history.replace({ pathname, hash, state: { filter: HOSTELS[queryHostel].cc, hostel: queryHostel } });
	}, [queryHostel, history, pathname, hash]);
	return (
		<>
			<Hero />
			<Builder initialFilter={state.filter ?? (queryHostel ? HOSTELS[queryHostel].cc : FILTER_ROUTE)} initialHostel={state.hostel ?? queryHostel} />

			<section id="trips" className={styles.trips} aria-labelledby="mm-trips-h">
				<div className={shared.wrap}>
					<div className={shared.secHead}>
						<span className={shared.eyebrow}>Trip ideas</span>
						<h2 className={shared.h2} id="mm-trips-h">
							The ideal backpacking trips, <span className={shared.hl}>hostel to hostel</span>
						</h2>
						<p className={shared.lede}>
							Five routes where every night is at a Mad Monkey. Open one to see the days, tweak it in the route builder, then take it with you in Stippl.
						</p>
					</div>
					<div className={styles.tripGrid}>
						{TRIPS.map((t, i) => (
							<TripCard key={t.id} trip={t} big={i === 0} />
						))}
					</div>
				</div>
			</section>

			<section className={shared.how} id="how" aria-labelledby="mm-how-h">
				<div className={shared.wrap}>
					<div className={shared.secHead}>
						<span className={shared.eyebrow}>How it works</span>
						<h2 className={shared.h2} id="mm-how-h">
							Plan the route in Stippl. <span className={shared.hl}>Live it at Mad Monkey.</span>
						</h2>
					</div>
					<div className={shared.steps}>
						<div className={shared.step}>
							<span className={shared.stepNum}>1</span>
							<h3>Pick a route</h3>
							<p>Start from a ready-made route or tap your own together in the route builder.</p>
							<div className={shared.screen}>
								<div className={shared.phone}>
									<img src={asset("img/trip-screen.webp")} alt="The Banana Pancake Trail in the Stippl app" loading="lazy" />
								</div>
							</div>
						</div>
						<div className={shared.step}>
							<span className={shared.stepNum}>2</span>
							<h3>Make it yours in Stippl</h3>
							<p>Set the nights, move stops around, add one, and plan it with the friends you met at The Meet Up. Every stop stays on your map, offline too.</p>
							<div className={shared.screen}>
								<div className={shared.phone}>
									<img src={asset("img/planner-screen.webp")} alt="A Mad Monkey route in the Stippl planner" loading="lazy" />
								</div>
							</div>
						</div>
						<div className={`${shared.step} ${shared.stepMm}`}>
							<span className={shared.stepNum}>3</span>
							<h3>Meet the crew at every stop</h3>
							<p>Every Mad Monkey runs The Meet Up every evening, so each stop on your route starts with new people. Come alone, leave together.</p>
							<div className={shared.screen}>
								<img src={asset("photos/phnom-penh-0.jpg")} alt="Pool party at Mad Monkey Phnom Penh" loading="lazy" />
								<a className={`${shared.btn} ${shared.btnLime}`} href={MM_HOME_URL} rel="noopener">
									Meet Mad Monkey <Arrow />
								</a>
							</div>
						</div>
					</div>
				</div>
			</section>

			<section id="faq" aria-labelledby="mm-faq-h">
				<div className={shared.wrap}>
					<div className={shared.secHead}>
						<span className={shared.eyebrow}>FAQ</span>
						<h2 className={shared.h2} id="mm-faq-h">
							Before you pack
						</h2>
					</div>
					<div className={shared.faqList}>
						{FAQ.map(([question, answer]) => (
							<details key={question}>
								<summary>{question}</summary>
								<p>{answer}</p>
							</details>
						))}
					</div>
				</div>
			</section>

			<section aria-labelledby="mm-cta-h" style={{ paddingTop: 0 }}>
				<div className={shared.wrap}>
					<div className={shared.ctaBox}>
						<div>
							<h2 id="mm-cta-h">Your Southeast Asia route, sorted before the first beer</h2>
							<p>Build it hostel to hostel, take it with you in Stippl, and see you at The Meet Up.</p>
						</div>
						<div className={shared.ctaBtns}>
							<Link id="mm-cta-build" className={`${shared.btn} ${shared.btnLime}`} to={{ pathname: HOME_PATH, hash: "#hostels" }}>
								Build your route
							</Link>
							<a id="mm-cta-app" className={`${shared.btn} ${shared.btnGhost}`} href={appUrl("cta")}>
								Get Stippl, free
							</a>
						</div>
					</div>
				</div>
			</section>
		</>
	);
};

export default MadMonkeyHome;
